import { useCallback } from "react"
import { useSelector } from "react-redux"

import Media from "components/ui/media"
import Shimmer from "components/ui/shimmer"
import { IS_JEWLR, isTestMode, IS_UK, IS_CA, IS_US } from "helpers/application"
import { to_currency } from "helpers/number"
import scrollToElement from "helpers/scroll-to-element"
import { cn } from "styles/utils"

import ProductPrice from "../product-price"

import Badge from "./badge"
import Financing from "./financing"
import PromoDesktop from "./promo-desktop"
import Ratings from "./ratings"

const WizardHeader = () => {
  const price = useSelector((state) =>
    Object.keys(state.wizardUI.currentPricing || {}).length > 0
      ? state.wizardUI.currentPricing.price_to_display * 100
      : 0
  )
  const title = useSelector((state) =>
    state.wizard.loading ? "" : state.wizard.title
  )
  const wizardLoading = useSelector((state) => state.wizard.loading)
  const hideMiniview = useSelector(
    (state) => state.previewImageUI.miniview.scrollHide
  )
  const isBestSellersSale = useSelector((state) =>
    (state.wizard.tags || []).includes("best-sellers-sale")
  )
  const isEverydayRings = useSelector((state) =>
    (state.wizard.tags || []).includes("everyday-rings-promo")
  )
  const isNecklaces = useSelector((state) =>
    (state.wizard.tags || []).includes("necklaces")
  )

  const handleRatingsClick = useCallback(() => {
    scrollToElement("#product-reviews", hideMiniview ? 90 : 350)
    if (!isTestMode) {
      dataLayer.push({
        event: "clicked-reviews-count-pdp",
      })
    }
  }, [hideMiniview])

  const financing = <Financing price={price} />
  const ratings = <Ratings handleClick={handleRatingsClick} />
  const everydayRingsCouponCode = IS_US()
    ? "RING15"
    : IS_CA()
      ? "EVERYDAY15"
      : IS_UK()
        ? "STYLE15"
        : "MYRING15"
  const everydayRingsMinAmount = IS_US()
    ? 150
    : IS_CA()
      ? 200
      : IS_UK()
        ? 120
        : 230
  const necklacesCouponCode = IS_US()
    ? "NECKLACE15"
    : IS_CA()
      ? "SALE15"
      : IS_UK()
        ? "FALL15"
        : "SPRING15"
  const necklacesMinAmount = IS_US() ? 100 : IS_CA() ? 135 : IS_UK() ? 75 : 150

  return (
    <section className="tablet:mb-12 mb-16">
      <PromoDesktop />
      {IS_JEWLR() && (
        <Media greaterThanOrEqual="tablet">
          <Badge />
        </Media>
      )}
      {wizardLoading ? (
        <Shimmer
          className="tablet:h-(--tablet-height) tablet-w-(--tablet-width) h-(--base-height) mb-8 w-full rounded-sm"
          style={{
            "--base-height": "42px",
            "--tablet-height": "28px",
            "--tablet-width": "320px",
          }}
        />
      ) : (
        <>
          <h1 className="text-18 tablet:text-24 tablet:font-light leading-22 tablet:leading-28 font-bold">
            {title}
          </h1>
          {IS_JEWLR() && <Media lessThan="tablet">{ratings}</Media>}
        </>
      )}
      <ProductPrice />
      {IS_JEWLR() && (
        <>
          {wizardLoading ? (
            <Shimmer
              className={cn(
                "h-(--base-height) tablet:mt-12 mt-16 w-full rounded-sm",
                sl.config.klarna_osm_pdp && "tablet:h-(--tablet-height)"
              )}
              style={{
                "--base-height": sl.config.klarna_osm_pdp ? "38px" : "18px",
                "--tablet-height": "21px",
              }}
            />
          ) : (
            <>
              {isBestSellersSale && (
                <div className="bg-jade leading-12 rounded-xs text-black-print text-12 mt-8 inline-flex items-center justify-center p-8 font-semibold">
                  Extra 15% OFF* with Code: DEAL15
                </div>
              )}
              {sl.config.sale_key.includes("2025_everyday_rings_flash") &&
                isEverydayRings && (
                  <div className="bg-jade leading-12 rounded-xs text-black-print text-12 mt-8 inline-flex items-center justify-center p-8 font-semibold">
                    Extra 15% OFF* with Code: {everydayRingsCouponCode} |{" "}
                    {to_currency(everydayRingsMinAmount)}+
                  </div>
                )}
              {sl.config.sale_key.includes("2025_necklaces_flash_sale") &&
                isNecklaces && (
                  <div className="bg-jade leading-12 rounded-xs text-black-print text-12 mt-8 inline-flex items-center justify-center p-8 font-semibold">
                    Extra 15% OFF* with Code: {necklacesCouponCode} |{" "}
                    {to_currency(necklacesMinAmount)}+
                  </div>
                )}
              <div
                className={cn(
                  "min-h-(--base-min-height) tablet:mt-12 mt-16",
                  sl.config.klarna_osm_pdp &&
                    "tablet:min-h-(--tablet-min-height)"
                )}
                style={{
                  "--base-min-height": sl.config.klarna_osm_pdp
                    ? "38px"
                    : "18px",
                  "--tablet-min-height": "21px",
                }}
              >
                <Media lessThan="tablet"> {financing}</Media>
                <Media
                  className={cn(
                    "flex justify-between gap-8",
                    sl.config.klarna_osm_pdp ? "items-center" : "items-end"
                  )}
                  greaterThanOrEqual="tablet"
                >
                  {ratings}
                  {financing}
                </Media>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

export default WizardHeader
